import React, { useEffect, useState } from "react";
import { ArrowUpRight } from "lucide-react";
import { useI18n } from "../context/I18nContext";
import { api, mailto } from "../lib/api";
import Seo from "../components/Seo";

export default function Approach() {
  const { lang, t } = useI18n();
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await api.get("/settings");
        setSettings(s.data);
      } catch (e) {
        console.error(e);
      }
    })();
  }, []);

  const aboutText = settings
    ? lang === "nl"
      ? settings.about_us_nl
      : settings.about_us_en
    : t.approach.aboutText;

  return (
    <div data-testid="page-approach" className="stoel-container pt-12 md:pt-20 pb-24">
      <Seo
        path="/aanpak"
        titleNl="Hoe wij werken"
        titleEn="How we work"
        descNl="Een tastbaar proces in vijf stappen: intake, plaatsbezoek, curatie, inkoop, plaatsing. Geen account­managers, geen tussenpartijen."
        descEn="A tangible process in five steps: intake, site visit, curation, procurement, placement. No account managers, no middlemen."
      />
      <header className="mb-20 max-w-3xl">
        <div className="stoel-eyebrow mb-4">— {t.nav.approach}</div>
        <h1 className="stoel-display text-5xl md:text-6xl lg:text-7xl">{t.approach.title}</h1>
        <p className="mt-6 text-lg text-stoel-inkSoft leading-relaxed">
          {t.approach.subtitle}
        </p>
      </header>

      {/* Process */}
      <section className="mb-24 md:mb-32">
        <div className="grid md:grid-cols-12 gap-10 mb-12">
          <div className="md:col-span-4">
            <h2 className="text-3xl md:text-4xl font-light tracking-tight">{t.approach.stepsTitle}</h2>
          </div>
        </div>

        <div className="space-y-10 md:space-y-16">
          {t.approach.processSteps.map((s, i) => (
            <div
              key={s.t}
              data-testid={`process-step-${i}`}
              className="grid md:grid-cols-12 gap-6 md:gap-10 pb-10 border-b border-stoel-line"
            >
              <div className="md:col-span-2">
                <div className="text-stoel-terracotta text-sm tracking-[0.3em] tabular-nums">
                  0{i + 1}
                </div>
              </div>
              <div className="md:col-span-4">
                <h3 className="text-2xl md:text-3xl font-light text-stoel-ink leading-tight">
                  {s.t}
                </h3>
              </div>
              <div className="md:col-span-6">
                <p className="text-base md:text-lg text-stoel-inkSoft leading-relaxed">
                  {s.b}
                </p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Audience */}
      <section className="mb-24 md:mb-32 bg-stoel-paperDeep/60 -mx-6 md:-mx-10 lg:-mx-16 px-6 md:px-10 lg:px-16 py-16 md:py-20">
        <div className="grid md:grid-cols-12 gap-10 mb-10">
          <div className="md:col-span-5">
            <div className="stoel-eyebrow mb-3">— Cliënten</div>
            <h2 className="text-3xl md:text-4xl font-light tracking-tight">{t.approach.audienceTitle}</h2>
          </div>
          <div className="md:col-span-6 md:col-start-7 flex items-end">
            <p className="text-stoel-inkSoft max-w-md">{t.approach.audienceIntro}</p>
          </div>
        </div>

        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 md:gap-8">
          {t.approach.audience.map((a, i) => (
            <div
              key={a.t}
              data-testid={`audience-${i}`}
              className="bg-stoel-paper p-6 md:p-8 border border-stoel-line/60"
            >
              <div className="text-stoel-terracotta text-xs tracking-[0.3em] mb-4 tabular-nums">
                0{i + 1}
              </div>
              <h3 className="text-xl font-light mb-3">{a.t}</h3>
              <p className="text-sm text-stoel-inkSoft leading-relaxed">{a.b}</p>
            </div>
          ))}
        </div>
      </section>

      {/* About us */}
      <section className="grid md:grid-cols-12 gap-10 mb-16">
        <div className="md:col-span-5">
          <div className="stoel-eyebrow mb-3">— Studio</div>
          <h2 className="stoel-display text-4xl md:text-5xl">{t.approach.aboutTitle}</h2>
        </div>
        <div className="md:col-span-7">
          <p className="text-xl md:text-2xl font-light leading-relaxed whitespace-pre-line">
            {aboutText}
          </p>
          <a
            href={mailto(t.contact.mailtoSubject)}
            data-testid="about-mail-info"
            className="mt-8 stoel-link inline-flex items-center gap-2 text-stoel-ink"
          >
            info@destoel.com <ArrowUpRight size={14} />
          </a>
        </div>
      </section>

      {/* Final CTA */}
      <section className="bg-stoel-ink text-stoel-paper -mx-6 md:-mx-10 lg:-mx-16 px-6 md:px-10 lg:px-16 py-16 md:py-20 flex flex-col md:flex-row md:items-center md:justify-between gap-6">
        <h3 className="text-3xl md:text-4xl font-light tracking-tight max-w-xl">
          {t.home.ctaSection}
        </h3>
        <a
          href={mailto(t.contact.mailtoSubject)}
          data-testid="approach-cta"
          className="inline-flex items-center gap-3 px-7 py-4 bg-stoel-terracotta hover:bg-stoel-terracottaDeep transition-colors text-sm tracking-wide"
        >
          {t.common.cta} <ArrowUpRight size={16} />
        </a>
      </section>
    </div>
  );
}
